import styled$1 from 'styled-components';
import React$1, { useRef, useEffect } from 'react';
import { Scene, PerspectiveCamera, WebGLRenderer, TorusGeometry, MeshPhongMaterial, DoubleSide, Mesh, AmbientLight, DirectionalLight } from 'three';
import { OrbitControls as OrbitControls$1 } from 'three/examples/jsm/controls/OrbitControls';

const StyledThreeDVisualizer = styled$1.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 100vh;
  width: 100vw;
  z-index: 100;
  @media (max-width: 768px) {
    left: -2rem;
    pointer-events: none;
    touch-action: auto;
  }
`;

const ThreeDVisualizer = () => {
  const containerRef = useRef(null);
  useEffect(() => {
    const container = containerRef.current;
    let dataArray = [];
    let currentFrame = 0;
    let frameInterval;
    let previousData = [];
    let isMobile = window.innerWidth < 768;
    const params = {
      waveAmplitude: 0.3,
      waveSpeed: 0.008,
      waveDivisor: 15000,
      waveFrequency: 0.5,
      smoothingFactor: 0.3,
      bassBoost: 1.5,
      torusSize: isMobile ? 5 : 10,
      torusThickness: isMobile ? 1.5 : 3,
      cameraDistance: isMobile ? 15 : 25
    };

    async function loadFrequencyData() {
      try {
        const response = await fetch('fork/json/frequency_data.json');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');

        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('Received non-JSON response');
        }

        const data = await response.json();
        return data;
      } catch (error) {
        console.error('Error loading frequency data:', error);
        return [];
      }
    }

    async function initVisualization() {
      dataArray = await loadFrequencyData();

      if (dataArray.length > 0) {
        frameInterval = setInterval(() => {
          currentFrame = (currentFrame + 1) % dataArray.length;
        }, 16);
      }
    }

    const scene = new Scene();
    const camera = new PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
    const renderer = new WebGLRenderer({
      antialias: true,
      alpha: true
    });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(window.innerWidth, window.innerHeight);
    container.appendChild(renderer.domElement);
    const geometry = new TorusGeometry(params.torusSize, params.torusThickness, 32, 32);
    const material = new MeshPhongMaterial({
      color: 0x000000,
      wireframe: true,
      side: DoubleSide
    });
    const sphereMesh = new Mesh(geometry, material);
    sphereMesh.rotation.x = Math.PI / 2;
    scene.add(sphereMesh); // Add a light source

    const ambientLight = new AmbientLight(0xffffff, 0.9);
    scene.add(ambientLight);
    const directionalLight = new DirectionalLight(0xffffff, 1);
    directionalLight.position.set(0, 1, 1).normalize();
    scene.add(directionalLight);
    camera.position.set(params.cameraDistance, 0, 0);
    camera.lookAt(0, 0, 0);
    const controls = new OrbitControls$1(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.25;
    controls.enableZoom = false;
    initVisualization();

    function animate() {
      requestAnimationFrame(animate);

      if (dataArray.length > 0) {
        const positionAttribute = geometry.attributes.position;
        const currentData = dataArray[currentFrame];

        if (previousData.length === 0) {
          previousData = [...currentData];
        }

        for (let i = 0; i < positionAttribute.count; i += 1) {
          const x = positionAttribute.getX(i);
          const z = positionAttribute.getZ(i);
          const frequencyIndex = i % currentData.length;
          const frequency = (frequencyIndex / currentData.length) ** 1.5 * currentData.length;
          const dataIndex = Math.floor(frequency);
          const currentValue = currentData[dataIndex] || 0;
          previousData[dataIndex] = previousData[dataIndex] || 0;
          const smoothedValue = currentValue * (1 - params.smoothingFactor) + previousData[dataIndex] * params.smoothingFactor;
          previousData[dataIndex] = smoothedValue;
          const bassBoostFactor = dataIndex < currentData.length / 4 ? params.bassBoost : 1;
          const waveHeight = Math.log(1 + smoothedValue / params.waveDivisor) * params.waveAmplitude * bassBoostFactor;
          const newZ = z + Math.sin(x * params.waveFrequency + Date.now() * params.waveSpeed) * waveHeight * (isMobile ? 2.5 : 4.0);
          positionAttribute.setZ(i, newZ);
        }

        positionAttribute.needsUpdate = true;
      }

      sphereMesh.rotation.z += 0.0002;
      controls.update();
      renderer.render(scene, camera);
    }

    animate();

    const onWindowResize = () => {
      isMobile = window.innerWidth < 768;
      params.torusSize = isMobile ? 5 : 10;
      params.torusThickness = isMobile ? 1.5 : 3;
      params.cameraDistance = isMobile ? 15 : 25;
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.position.set(params.cameraDistance, 0, 0);
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener('resize', onWindowResize, false);
    return () => {
      window.removeEventListener('resize', onWindowResize);
      if (frameInterval) clearInterval(frameInterval);
    };
  }, []);
  return /*#__PURE__*/React$1.createElement(StyledThreeDVisualizer, {
    ref: containerRef
  });
};

ThreeDVisualizer.propTypes = {};
ThreeDVisualizer.defaultProps = {};

export default ThreeDVisualizer;
